import { useDeferredValue, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ReceiptText } from 'lucide-react';
import {
  PERMISSIONS,
  PurchaseInvoiceStatus,
  SupplierActiveFilter,
  type PurchaseInvoiceSummary,
} from '@optiflow/types';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { PageHeader } from '@/components/ui/PageHeader';
import { Pagination } from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';
import { useBranch } from '@/hooks/useBranch';
import { useCurrency } from '@/hooks/useCurrency';
import { usePermissions } from '@/hooks/usePermissions';
import { apiErrorMessage } from '@/services/apiClient';
import { usePurchaseInvoicesQuery } from '../api/purchase-invoices.api';
import { useSuppliersQuery } from '../api/suppliers.api';
import { PurchaseInvoiceStatusBadge } from '../components/PurchaseInvoiceStatusBadge';

const PAGE_SIZE = 20;

/**
 * What the business owes its suppliers for goods already received.
 *
 * The amount shown is the obligation, not the invoice total: a voided document still lists, with
 * its figures intact, but it asks for nothing and reads as nothing owed.
 */
export function SupplierInvoicesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { branches } = useBranch();
  const { format } = useCurrency();
  const { can } = usePermissions();

  const [search, setSearch] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [branchId, setBranchId] = useState('');
  const [status, setStatus] = useState<PurchaseInvoiceStatus | ''>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);

  const deferredSearch = useDeferredValue(search);
  const { data, isPending, isError, error, refetch, isFetching } = usePurchaseInvoicesQuery({
    page,
    pageSize: PAGE_SIZE,
    search: deferredSearch,
    supplierId,
    branchId,
    status,
    dateFrom,
    dateTo,
  });

  const suppliers = useSuppliersQuery({ pageSize: 100, active: SupplierActiveFilter.ALL });
  const canRecord = can(PERMISSIONS.PURCHASING_INVOICE);

  const onFilter =
    <T,>(set: (value: T) => void) =>
    (value: T) => {
      set(value);
      setPage(1);
    };

  const columns: Column<PurchaseInvoiceSummary>[] = [
    {
      key: 'reference',
      header: t('purchasing.invoices.fields.reference'),
      primary: true,
      render: (invoice) => (
        <span className="flex flex-col">
          <span className="text-ink font-mono font-medium" dir="ltr">
            {invoice.reference}
          </span>
          <span className="text-muted text-xs" dir="auto">
            {invoice.supplierName}
          </span>
        </span>
      ),
    },
    {
      key: 'supplierInvoiceNumber',
      header: t('purchasing.invoices.fields.supplierInvoiceNumber'),
      render: (invoice) => (
        <span className="text-muted font-mono text-sm" dir="ltr">
          {invoice.supplierInvoiceNumber}
        </span>
      ),
    },
    {
      key: 'invoiceDate',
      header: t('purchasing.invoices.fields.invoiceDate'),
      render: (invoice) => (
        <span className="text-ink" dir="ltr">
          {invoice.invoiceDate}
        </span>
      ),
    },
    {
      key: 'dueDate',
      header: t('purchasing.invoices.fields.dueDate'),
      render: (invoice) => (
        <span className="text-ink" dir="ltr">
          {invoice.dueDate}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('purchasing.invoices.fields.status'),
      render: (invoice) => <PurchaseInvoiceStatusBadge status={invoice.status} />,
    },
    {
      key: 'obligation',
      header: t('purchasing.invoices.fields.obligation'),
      align: 'end',
      render: (invoice) => (
        <span className="flex flex-col items-end">
          <span className="text-ink font-medium" dir="ltr">
            {format(invoice.obligationMinor)}
          </span>
          {invoice.status === PurchaseInvoiceStatus.VOIDED ? (
            <span className="text-muted text-xs" dir="ltr">
              {t('purchasing.invoices.wasTotal', { total: format(invoice.totalMinor) })}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      align: 'end',
      render: (invoice) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void navigate(`/purchasing/invoices/${invoice.id}`)}
          aria-label={t('purchasing.invoices.openNamed', { reference: invoice.reference })}
        >
          {t('common.view')}
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('purchasing.invoices.title')}
        subtitle={t('purchasing.invoices.subtitle')}
        actions={
          canRecord ? (
            <Button onClick={() => void navigate('/purchasing/invoices/new')}>
              {t('purchasing.invoices.record')}
            </Button>
          ) : null
        }
      />

      <Card>
        <CardHeader
          title={t('purchasing.invoices.title')}
          subtitle={
            data ? t('purchasing.invoices.count', { count: data.pagination.totalItems }) : undefined
          }
        />

        <div className="border-line grid gap-3 border-b px-4 py-3 sm:grid-cols-2 lg:grid-cols-3">
          <Input
            type="search"
            value={search}
            onChange={(event) => onFilter(setSearch)(event.target.value)}
            placeholder={t('purchasing.invoices.searchPlaceholder')}
            aria-label={t('purchasing.invoices.searchLabel')}
          />
          <Select
            options={[
              { value: '', label: t('purchasing.orders.filters.allSuppliers') },
              ...(suppliers.data?.items ?? []).map((supplier) => ({
                value: supplier.id,
                label: supplier.name,
              })),
            ]}
            value={supplierId}
            aria-label={t('purchasing.orders.fields.supplier')}
            onChange={(event) => onFilter(setSupplierId)(event.target.value)}
          />
          <Select
            options={[
              { value: '', label: t('purchasing.orders.filters.allBranches') },
              ...branches.map((branch) => ({ value: branch.id, label: branch.name })),
            ]}
            value={branchId}
            aria-label={t('purchasing.orders.fields.branch')}
            onChange={(event) => onFilter(setBranchId)(event.target.value)}
          />
          <Select
            options={[
              { value: '', label: t('purchasing.invoices.filters.allStatuses') },
              ...Object.values(PurchaseInvoiceStatus).map((value) => ({
                value,
                label: t(`purchasing.invoices.status.${value}`),
              })),
            ]}
            value={status}
            aria-label={t('purchasing.invoices.fields.status')}
            onChange={(event) =>
              onFilter(setStatus)(event.target.value as PurchaseInvoiceStatus | '')
            }
          />
          <Input
            type="date"
            value={dateFrom}
            onChange={(event) => onFilter(setDateFrom)(event.target.value)}
            aria-label={t('purchasing.orders.filters.dateFrom')}
          />
          <Input
            type="date"
            value={dateTo}
            onChange={(event) => onFilter(setDateTo)(event.target.value)}
            aria-label={t('purchasing.orders.filters.dateTo')}
          />
        </div>

        <DataTable
          caption={t('purchasing.invoices.tableCaption')}
          columns={columns}
          rows={data?.items}
          rowKey={(invoice) => invoice.id}
          isLoading={isPending}
          isError={isError}
          errorMessage={apiErrorMessage(error) ?? t('purchasing.invoices.loadError')}
          onRetry={() => void refetch()}
          emptyIcon={ReceiptText}
          emptyTitle={t('purchasing.invoices.empty')}
          emptyDescription={t('purchasing.invoices.emptyHint')}
        />

        {data ? (
          <Pagination pagination={data.pagination} onPageChange={setPage} isBusy={isFetching} />
        ) : null}
      </Card>
    </div>
  );
}
